import React, { createContext, useContext, useState, useEffect } from 'react';

// Import translations
import el from '../translations/el.json';
import de from '../translations/de.json';
import en from '../translations/en.json';

const translations = { el, de, en };

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language from localStorage or default to English
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
    // Update HTML lang attribute
    document.documentElement.lang = language;
  }, [language]);

  // Translation function
  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return fallback || key;
      }
    }
    
    // Return empty string if value is explicitly empty, otherwise return value or fallback
    if (value === '') return '';
    return value !== undefined ? value : (fallback || key);
  };

  // Get nested object for arrays (like features, faqs, etc.)
  const tArray = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return [];
      }
    }
    
    return Array.isArray(value) ? value : [];
  };

  // Get entire section as object
  const tSection = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return {};
      }
    }
    
    return value || {};
  };

  const value = {
    language,
    setLanguage,
    t,
    tArray,
    tSection,
    languages: [
      { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
      { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
      { code: 'en', name: 'English', flag: '🇺🇸' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
